import type { PropsWithChildren } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { createInstance } from 'i18next';
import { I18nextProvider } from 'react-i18next';

const NAMESPACE = 'app';

type Dictionary = Record<string, string>;
export type DictionaryByLang = Record<string, Dictionary>;

export type WidgetI18nProvider = PropsWithChildren<{
  dictionary: DictionaryByLang;
  language: string;
  override: DictionaryByLang;
}>;

export function WidgetI18nProvider({
  children,
  dictionary,
  language,
  override = {}
}: WidgetI18nProvider) {
  const dictionaryRef = useRef(dictionary);

  const i18n = useMemo(() => createInstance(), []);
  useMemo(() => {
    i18n.init({
      defaultNS: NAMESPACE,
      react: {
        useSuspense: false
      },
      lowerCaseLng: true,
      nsSeparator: false,
      keySeparator: false,
      interpolation: {
        escapeValue: false
      }
    });
  }, [i18n]);

  useEffect(() => {
    for (const [langKey, langDictionary] of Object.entries(
      dictionaryRef.current
    )) {
      i18n.addResourceBundle(langKey, NAMESPACE, langDictionary);
    }
  }, [i18n]);

  useEffect(() => {
    if (!language) return;

    i18n.changeLanguage(language);
    for (const [langKey, langOverrides] of Object.entries(override)) {
      i18n.addResourceBundle(
        langKey,
        NAMESPACE,
        { ...dictionaryRef.current[langKey], ...langOverrides },
        true,
        true
      );
    }
  }, [i18n, language, override]);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
